import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

/**
 * Error boundary specifically for lazy-loaded components
 * Handles chunk loading failures and provides retry mechanism
 */
export class LazyLoadErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    retryCount: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if it's a chunk loading error
    const isChunkError = 
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.name === 'ChunkLoadError' ||
      error.message?.includes('import()');

    return { 
      hasError: true, 
      error,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('LazyLoadErrorBoundary caught an error:', error, errorInfo);
    
    // Log chunk loading errors specifically
    if (error.message?.includes('Failed to fetch dynamically imported module')) {
      console.error('Dynamic import failure - this may be a build/deployment issue');
    }
  }

  private handleRetry = () => {
    // Force a hard reload to clear module cache
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      const isChunkError = 
        this.state.error?.message?.includes('Failed to fetch dynamically imported module') ||
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.name === 'ChunkLoadError';

      if (isChunkError) {
        return (
          <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="max-w-md w-full border-4 border-foreground bg-card shadow-neo p-8 text-center space-y-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-destructive/20 border-4 border-destructive">
                <AlertTriangle className="w-10 h-10 text-destructive" strokeWidth={3} />
              </div>
              
              <div className="space-y-2">
                <h1 className="font-heavy text-3xl uppercase text-foreground">
                  Module Load Failed
                </h1>
                <p className="text-muted-foreground font-body">
                  Failed to load page resources. This may be a temporary network issue.
                </p>
              </div>

              <div className="flex flex-col gap-3 justify-center">
                <Button
                  onClick={this.handleRetry}
                  className="border-2 border-foreground shadow-neo font-heavy uppercase bg-primary"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reload Page
                </Button>
                <p className="text-xs text-muted-foreground">
                  If this persists, try clearing your browser cache or contact support.
                </p>
              </div>
            </div>
          </div>
        );
      }

      // Fall back to default error boundary if provided
      return this.props.fallback || (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <p className="text-muted-foreground">An error occurred loading this page.</p>
            <Button onClick={this.handleRetry} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
